import { ReactElement, ReactNode } from "react";
import { t } from "@lingui/core/macro";

import { Modal } from "_/components/modal";
import { Button } from "_/components/button";

import * as S from "./styled";

export type WizardStepDef<T extends string> = {
  /** Unique key identifying this step. */
  key: T;
  /** Human-readable title shown in the progress bar. */
  title: string;
  /** Content rendered when this step is active. */
  content: ReactNode;
  /**
   * When set to false the step is excluded from the numbered progress bar
   * but still participates in navigation. Defaults to true.
   */
  enumerated?: boolean;
};

type WizardProps<T extends string> = {
  /** Modal title. */
  title: string;
  /** Called when the user closes the modal or clicks Cancel. */
  onClose: () => void;
  /**
   * Ordered list of steps currently visible in the wizard. For branching
   * workflows the parent can recompute this array based on earlier choices —
   * only the steps present here appear in the progress bar and are reachable
   * via default navigation.
   */
  steps: WizardStepDef<T>[];
  /** Key of the currently active step. */
  step: T;
  /** Called when the wizard navigates to a different step. */
  onStepChange: (step: T) => void;
  /**
   * Given the current step key, returns the key to navigate to when Back is
   * pressed. Defaults to the previous step in the `steps` array.
   */
  onBack?: (current: T) => T;
  /**
   * Given the current step key, returns the key to navigate to when Next is
   * pressed. Defaults to the next step in the `steps` array.
   */
  onNext?: (current: T) => T;
  /**
   * Override automatic Back-button visibility.
   * Defaults to true when the current step is not the first step.
   */
  showBack?: boolean;
  /**
   * Override automatic Next-button visibility. Defaults to true when the
   * current step is not the last step and `onSubmit` is not provided.
   */
  showNext?: boolean;
  /** Controls whether the Next button is enabled. Defaults to true. */
  canAdvance?: boolean;
  /** If provided, a submit button is shown on the last step. */
  onSubmit?: () => void;
  /** Label for the submit button. Defaults to "Submit". */
  submitLabel?: string;
  /** Disables the submit button and shows a loading label. */
  isSubmitting?: boolean;
};

/**
 * Generic multi-step wizard shell rendered inside a full-screen modal.
 *
 * The parent owns the current-step state and passes it via `step` /
 * `onStepChange`. The `steps` array defines the canonical ordering and
 * carries the content for each step. The wizard renders a numbered progress
 * bar and the active step's content automatically.
 *
 * For branching flows the parent can dynamically adjust the `steps` array
 * (e.g. omitting steps that don't apply) and/or override `onBack` / `onNext`
 * to jump to non-adjacent steps.
 */
export function Wizard<T extends string>({
  title,
  steps,
  step,
  showBack,
  showNext,
  canAdvance = true,
  submitLabel = t`common.submit`,
  isSubmitting = false,
  onStepChange,
  onBack,
  onNext,
  onSubmit,
  onClose,
}: WizardProps<T>): ReactElement {
  const currentIndex = Math.max(
    steps.findIndex((s) => s.key === step),
    0
  );

  const defaultBack = (current: T): T => {
    const idx = steps.findIndex((s) => s.key === current);
    const prev = Math.max(0, idx - 1);
    return steps[prev].key;
  };

  const defaultNext = (current: T): T => {
    const idx = steps.findIndex((s) => s.key === current);
    const next = Math.min(steps.length - 1, idx + 1);
    return steps[next].key;
  };

  const handleBack = () => {
    const resolver = onBack ?? defaultBack;
    onStepChange(resolver(step));
  };

  const handleNext = () => {
    const resolver = onNext ?? defaultNext;
    onStepChange(resolver(step));
  };

  const isFirst = currentIndex <= 0;
  const isLast = currentIndex >= steps.length - 1;

  const backVisible = showBack ?? !isFirst;
  const nextVisible = showNext ?? !isLast;

  const activeStepDef = steps.find((s) => s.key === step);
  const activeContent = activeStepDef?.content;

  // Hide the progress bar only when no enumerated step has been reached yet.
  const showProgress = steps
    .slice(0, currentIndex + 1)
    .some((s) => s.enumerated !== false);

  const progressSteps = steps
    .filter((s) => s.enumerated !== false)
    .map((s, i) => {
      const stepIndex = steps.findIndex((all) => all.key === s.key);
      const completed = stepIndex < currentIndex;
      const active = stepIndex === currentIndex;

      return (
        <S.ProgressStep key={s.key}>
          <S.ProgressCircle $completed={completed} $active={active}>
            {i + 1}
          </S.ProgressCircle>
          <S.ProgressLabel $completed={completed} $active={active}>
            {s.title}
          </S.ProgressLabel>
        </S.ProgressStep>
      );
    });

  return (
    <Modal title={title} open onClose={onClose}>
      {showProgress && <S.ProgressBar>{progressSteps}</S.ProgressBar>}
      {activeContent}
      <S.Nav>
        <div>
          {backVisible && (
            <Button kind="secondary" size="small" onClick={handleBack}>
              {t`common.back`}
            </Button>
          )}
        </div>
        <S.NavRight>
          <Button kind="secondary" size="small" onClick={onClose}>
            {t`common.cancel`}
          </Button>
          {onSubmit && isLast && (
            <Button size="small" onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting ? t`components.wizard.submitting` : submitLabel}
            </Button>
          )}
          {nextVisible && (
            <Button size="small" onClick={handleNext} disabled={!canAdvance}>
              {t`common.next`}
            </Button>
          )}
        </S.NavRight>
      </S.Nav>
    </Modal>
  );
}
